import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import api, { API_URL } from '../lib/api';
import {
  Package, Users, TruckIcon, DollarSign, Menu, LogOut,
  Boxes, Calendar, FileText, Wallet, QrCode, Settings,
  TrendingUp, ShoppingCart, ClipboardList, UserCog,
  Building2, BarChart3, Download, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import ZoomControl from '../components/ZoomControl';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await api.get('/settings/company');
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error loading company info');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/reports/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await api.get('/reports/envios/excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `reporte_envios_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report');
    }
  };

  const Navigation = () => (
    <nav className="space-y-2">
      <div className="px-3 py-2">
        <h2 className="mb-3 px-4 text-xl font-bold text-gray-900">Operaciones</h2>
        <div className="space-y-1">
          <Link to="/envios"><Button variant="ghost" className="w-full justify-start hover:bg-blue-50 h-14 text-lg"><Package className="mr-3 h-6 w-6 text-blue-600" /><span className="font-semibold">Envios</span></Button></Link>
          <Link to="/pickups"><Button variant="ghost" className="w-full justify-start hover:bg-orange-50 h-14 text-lg"><Calendar className="mr-3 h-6 w-6 text-orange-600" /><span className="font-semibold">Recogidas</span></Button></Link>
          <Link to="/calculadora"><Button variant="ghost" className="w-full justify-start hover:bg-blue-50 h-14 text-lg"><ClipboardList className="mr-3 h-6 w-6 text-blue-600" /><span className="font-semibold">Calculadora</span></Button></Link>
          <Link to="/remitentes"><Button variant="ghost" className="w-full justify-start hover:bg-green-50 h-14 text-lg"><Users className="mr-3 h-6 w-6 text-green-600" /><span className="font-semibold">Remitentes</span></Button></Link>
          <Link to="/destinatarios"><Button variant="ghost" className="w-full justify-start hover:bg-purple-50 h-14 text-lg"><Users className="mr-3 h-6 w-6 text-purple-600" /><span className="font-semibold">Destinatarios</span></Button></Link>
          <Link to="/tracking"><Button variant="ghost" className="w-full justify-start hover:bg-green-50 h-14 text-lg"><TruckIcon className="mr-3 h-6 w-6 text-green-600" /><span className="font-semibold">Rastreo Publico</span></Button></Link>
        </div>
      </div>
      <div className="px-3 py-2">
        <h2 className="mb-3 px-4 text-xl font-bold text-gray-900">Logistica</h2>
        <div className="space-y-1">
          <Link to="/furgones"><Button variant="ghost" className="w-full justify-start hover:bg-indigo-50 h-14 text-lg"><TruckIcon className="mr-3 h-6 w-6 text-indigo-600" /><span className="font-semibold">Furgones</span></Button></Link>
          <Link to="/scanner-qr"><Button variant="ghost" className="w-full justify-start hover:bg-pink-50 h-14 text-lg"><QrCode className="mr-3 h-6 w-6 text-pink-600" /><span className="font-semibold">Scanner QR</span></Button></Link>
        </div>
      </div>
      <div className="px-3 py-2">
        <h2 className="mb-3 px-4 text-xl font-bold text-gray-900">Finanzas</h2>
        <div className="space-y-1">
          <Link to="/reportes"><Button variant="ghost" className="w-full justify-start hover:bg-blue-50 h-14 text-lg"><BarChart3 className="mr-3 h-6 w-6 text-blue-600" /><span className="font-semibold">Reportes</span></Button></Link>
          <Link to="/cuadre-diario"><Button variant="ghost" className="w-full justify-start hover:bg-yellow-50 h-14 text-lg"><DollarSign className="mr-3 h-6 w-6 text-yellow-600" /><span className="font-semibold">Cuadre Diario</span></Button></Link>
          <Link to="/gastos"><Button variant="ghost" className="w-full justify-start hover:bg-red-50 h-14 text-lg"><TrendingUp className="mr-3 h-6 w-6 text-red-600" /><span className="font-semibold">Gastos</span></Button></Link>
          <Link to="/caja-chica"><Button variant="ghost" className="w-full justify-start hover:bg-teal-50 h-14 text-lg"><Wallet className="mr-3 h-6 w-6 text-teal-600" /><span className="font-semibold">Caja Chica</span></Button></Link>
        </div>
      </div>
      <div className="px-3 py-2">
        <h2 className="mb-3 px-4 text-xl font-bold text-gray-900">Recursos</h2>
        <div className="space-y-1">
          <Link to="/empleados"><Button variant="ghost" className="w-full justify-start hover:bg-cyan-50 h-14 text-lg"><UserCog className="mr-3 h-6 w-6 text-cyan-600" /><span className="font-semibold">Empleados</span></Button></Link>
          <Link to="/nomina"><Button variant="ghost" className="w-full justify-start hover:bg-emerald-50 h-14 text-lg"><FileText className="mr-3 h-6 w-6 text-emerald-600" /><span className="font-semibold">Nomina</span></Button></Link>
          <Link to="/suministros"><Button variant="ghost" className="w-full justify-start hover:bg-amber-50 h-14 text-lg"><ShoppingCart className="mr-3 h-6 w-6 text-amber-600" /><span className="font-semibold">Suministros</span></Button></Link>
        </div>
      </div>
      <div className="px-3 py-2">
        <h2 className="mb-3 px-4 text-xl font-bold text-gray-900">Sistema</h2>
        <div className="space-y-1">
          <Link to="/usuarios"><Button variant="ghost" className="w-full justify-start hover:bg-gray-100 h-14 text-lg"><Settings className="mr-3 h-6 w-6 text-gray-600" /><span className="font-semibold">Usuarios</span></Button></Link>
          <Link to="/configuracion"><Button variant="ghost" className="w-full justify-start hover:bg-slate-50 h-14 text-lg"><Building2 className="mr-3 h-6 w-6 text-slate-600" /><span className="font-semibold">Config. Empresa</span></Button></Link>
        </div>
      </div>
    </nav>
  );

  const maxIngresos = stats?.tendencia_7dias ? Math.max(...stats.tendencia_7dias.map(d => d.ingresos), 1) : 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-24">
      <header className="bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center space-x-4 w-full sm:w-auto">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden h-14 w-14"><Menu className="h-8 w-8" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 overflow-y-auto">
                <div className="py-4"><h2 className="text-2xl font-bold mb-4 px-4">Menu Principal</h2><Navigation /></div>
              </SheetContent>
            </Sheet>
            <img
              src={companyInfo?.logo_url ? `${API_URL.replace('/api', '')}${companyInfo.logo_url}?t=1` : '/logo.png'}
              alt="Logo"
              className="h-12 w-12 object-contain rounded-lg"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{companyInfo?.nombre || 'Sistema de Envios'}</h1>
              <p className="text-base text-gray-500 hidden sm:block">{companyInfo?.direccion || 'Gestion integral de logistica'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <ZoomControl />
            <Button variant="outline" onClick={downloadExcel} data-testid="download-excel-btn" className="h-12 text-base px-4">
              <Download className="mr-2 h-5 w-5" /> Excel
            </Button>
            <div className="text-right hidden sm:block">
              <p className="font-bold text-lg text-gray-900">{user?.nombre_completo}</p>
              <Badge variant="outline" className="text-base">{user?.role}</Badge>
            </div>
            <Button variant="ghost" size="icon" onClick={logout} className="hover:bg-red-50 h-14 w-14" data-testid="logout-btn">
              <LogOut className="h-7 w-7 text-red-600" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          <aside className="hidden md:block w-72 flex-shrink-0">
            <Card className="sticky top-4 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
                <CardTitle className="text-lg">Navegacion</CardTitle>
              </CardHeader>
              <CardContent className="p-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                <Navigation />
              </CardContent>
            </Card>
          </aside>

          <main className="flex-1">
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-testid="dashboard-title">Dashboard</h2>
              <p className="text-lg text-gray-600">Vista general del sistema de envios</p>
            </div>

            {loading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div></div>
            ) : stats ? (
              <>
                {/* KPI Cards */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
                  <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg" data-testid="stat-envios">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-base font-semibold">Total Envios</CardTitle>
                      <Package className="h-7 w-7 opacity-80" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-4xl font-bold">{stats.totales.envios}</div>
                      <p className="text-base opacity-80 mt-1">{stats.totales.envios_hoy} hoy | {stats.totales.envios_mes} este mes</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg" data-testid="stat-ingresos">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-base font-semibold">Ingresos del Mes</CardTitle>
                      <DollarSign className="h-7 w-7 opacity-80" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-4xl font-bold">${stats.financiero.ingresos_mes.toLocaleString()}</div>
                      <p className="text-base opacity-80 mt-1">Cobrado: ${stats.financiero.cobrado_mes.toLocaleString()}</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-lg" data-testid="stat-pendiente">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-base font-semibold">Cuentas por Cobrar</CardTitle>
                      <ArrowDownRight className="h-7 w-7 opacity-80" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-4xl font-bold">${stats.financiero.pendiente_global.toLocaleString()}</div>
                      <p className="text-base opacity-80 mt-1">{stats.pagos.parcial || 0} parciales | {stats.pagos.pendiente || 0} pendientes</p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-lg" data-testid="stat-clientes">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <CardTitle className="text-base font-semibold">Clientes</CardTitle>
                      <Users className="h-7 w-7 opacity-80" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-4xl font-bold">{stats.totales.clientes}</div>
                      <p className="text-base opacity-80 mt-1">{stats.totales.empleados} empleados</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Financial Overview + Trend */}
                <div className="grid gap-6 lg:grid-cols-2 mb-6">
                  {/* 7 Day Trend Chart */}
                  <Card className="shadow-lg" data-testid="chart-trend">
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2"><BarChart3 className="h-6 w-6 text-blue-600" /> Tendencia 7 Dias</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-end gap-2 h-40">
                        {stats.tendencia_7dias.map((d, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <span className="text-sm font-bold text-gray-700">{d.envios}</span>
                            <div
                              className="w-full rounded-t-md bg-gradient-to-t from-blue-500 to-blue-400 transition-all duration-500"
                              style={{ height: `${Math.max((d.ingresos / maxIngresos) * 100, 4)}%`, minHeight: '4px' }}
                              title={`$${d.ingresos}`}
                            ></div>
                            <span className="text-sm text-gray-500">{d.fecha.slice(5)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between text-sm text-gray-400 mt-2 px-1">
                        <span>Envios por dia</span>
                        <span>Barras = ingresos</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Financial Summary */}
                  <Card className="shadow-lg" data-testid="financial-summary">
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2"><DollarSign className="h-6 w-6 text-green-600" /> Resumen Financiero (Mes)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-2"><ArrowUpRight className="h-5 w-5 text-green-600" /><span className="text-lg font-semibold">Ingresos</span></div>
                        <span className="text-2xl font-bold text-green-600">${stats.financiero.ingresos_mes.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-red-50 rounded-lg">
                        <div className="flex items-center gap-2"><ArrowDownRight className="h-5 w-5 text-red-600" /><span className="text-lg font-semibold">Gastos</span></div>
                        <span className="text-2xl font-bold text-red-600">${stats.financiero.gastos_mes.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center p-4 bg-amber-50 rounded-lg">
                        <div className="flex items-center gap-2"><DollarSign className="h-5 w-5 text-amber-600" /><span className="text-lg font-semibold">Pendiente</span></div>
                        <span className="text-2xl font-bold text-amber-600">${stats.financiero.pendiente_mes.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center p-5 bg-indigo-100 rounded-lg border-2 border-indigo-300">
                        <span className="text-xl font-bold">Utilidad Neta</span>
                        <span className="text-3xl font-bold text-indigo-700">${stats.financiero.utilidad_mes.toLocaleString()}</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Shipment Status + Top Customers */}
                <div className="grid gap-6 lg:grid-cols-2 mb-6">
                  {/* Status breakdown */}
                  <Card className="shadow-lg" data-testid="status-breakdown">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2"><Package className="h-5 w-5 text-indigo-600" /> Estado de Envios</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(stats.estados).map(([estado, count]) => {
                          const total = stats.totales.envios || 1;
                          const pct = Math.round((count / total) * 100);
                          const colors = {
                            pendiente: 'bg-yellow-400', recogido: 'bg-blue-400', en_transito: 'bg-cyan-400',
                            embarque_maritimo: 'bg-indigo-400', revision_aduana: 'bg-purple-400',
                            almacen_rd: 'bg-teal-400', en_furgon: 'bg-orange-400',
                            camino_entrega: 'bg-lime-400', entregado: 'bg-green-500', cancelado: 'bg-red-400'
                          };
                          return (
                            <div key={estado} className="flex items-center gap-3">
                              <span className="text-base w-32 truncate capitalize font-medium">{estado.replace(/_/g, ' ')}</span>
                              <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                                <div className={`h-full rounded-full ${colors[estado] || 'bg-gray-400'} transition-all`} style={{ width: `${pct}%` }}></div>
                              </div>
                              <span className="text-base font-bold w-10 text-right">{count}</span>
                            </div>
                          );
                        })}
                        {Object.keys(stats.estados).length === 0 && <p className="text-gray-400 text-sm text-center py-4">Sin datos</p>}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Top Customers */}
                  <Card className="shadow-lg" data-testid="top-customers">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2"><Users className="h-5 w-5 text-green-600" /> Top Clientes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {stats.top_clientes.map((c, i) => (
                          <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-bold ${i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-gray-400' : 'bg-orange-400'}`}>{i + 1}</div>
                              <div>
                                <p className="font-bold text-lg">{c.nombre}</p>
                                <p className="text-base text-gray-500">{c.envios} envios</p>
                              </div>
                            </div>
                            <span className="font-bold text-xl text-green-600">${c.total.toLocaleString()}</span>
                          </div>
                        ))}
                        {stats.top_clientes.length === 0 && <p className="text-gray-400 text-sm text-center py-4">Sin datos</p>}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Actions */}
                <Card className="shadow-lg" data-testid="quick-actions">
                  <CardHeader>
                    <CardTitle className="text-2xl">Acciones Rapidas</CardTitle>
                    <CardDescription className="text-base">Accede rapidamente a las funciones principales</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                      <Link to="/envios"><Button className="w-full h-20 text-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-md"><Package className="mr-3 h-7 w-7" />Crear Envio</Button></Link>
                      <Link to="/remitentes"><Button className="w-full h-20 text-xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-md"><Users className="mr-3 h-7 w-7" />Agregar Remitente</Button></Link>
                      <Link to="/furgones"><Button className="w-full h-20 text-xl bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 shadow-md"><TruckIcon className="mr-3 h-7 w-7" />Furgones</Button></Link>
                      <Link to="/cuadre-diario"><Button className="w-full h-20 text-xl bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 shadow-md"><DollarSign className="mr-3 h-7 w-7" />Cuadre del Dia</Button></Link>
                      <Link to="/tracking"><Button className="w-full h-20 text-xl bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-md"><TruckIcon className="mr-3 h-7 w-7" />Rastrear Envio</Button></Link>
                      <Button onClick={downloadExcel} className="w-full h-20 text-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 shadow-md"><Download className="mr-3 h-7 w-7" />Descargar Reporte</Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">Error al cargar estadisticas</div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
